<template>
  <div class="user-settings">
    <v-card class="user-settings__card">
      <v-card-title class="pa-0">
        Ustawienia Konta
      </v-card-title>

      <div class="d-flex mt-6">
        <div>
          <label class="input-label">Imię</label>
          <v-text-field
            :value="user.firstName"
            v-bind="props.disabledInput"
            class="user-settings__input mr-4"
          />
        </div>

        <div>
          <label class="input-label">Nazwisko</label>
          <v-text-field
            :value="user.lastName"
            v-bind="props.disabledInput"
            class="user-settings__input mr-4"
          />
        </div>

        <div>
          <label class="input-label">Adres e-mail</label>
          <v-text-field
            :value="user.email"
            v-bind="props.disabledInput"
            class="user-settings__input"
          />
        </div>
      </div>

      <div class="d-flex align-center mt-6">
        <v-switch
          v-model="emailNotifications"
          class="mr-4"
          small
          :disabled="isNotificationSwitchBlocked"
          hide-details
          @change="changeNotificationsSettings($event, 'emailNotifications')"
        />
        Powiadomienia e-mail

        <v-switch
          v-model="chatNotifications"
          class="mr-4 ml-12"
          small
          :disabled="isNotificationSwitchBlocked"
          hide-details
          @change="changeNotificationsSettings($event, 'chatNotifications')"
        />
        Powiadomienia czatu

        <v-switch
          v-model="ownActionsNotifications"
          class="mr-4 ml-12"
          small
          :disabled="isNotificationSwitchBlocked"
          hide-details
          @change="changeNotificationsSettings($event, 'ownActionsNotifications')"
        />
        Powiadomienia własnych działań
      </div>

      <div class="mt-6">
        <label class="input-label">Role</label>
        <div class="user-roles">
          <div
            v-for="role in user.roles"
            :key="role.id"
            class="mr-2 mb-2"
            color="white"
            text-color="black"
            small
          >
            &bull; {{ role.label }}
          </div>
        </div>
      </div>

      <div class="mt-6">
        <label class="input-label">Wersja WEB:</label>
        <div class="ml-1 pb-4">
          {{ frontendCommitHash }}
        </div>
        <label class="input-label">Wersja API:</label>
        <div class="ml-1">
          {{ backendCommitHash }}
        </div>
      </div>
    </v-card>
    <v-card class="user-settings__card mt-8">
      <v-card-title class="pa-0">
        Zmiana hasła
      </v-card-title>

      <v-form
        ref="userPasswordChangeForm"
        v-model="isFormValid"
        class="mt-6"
        @submit.prevent="submitPasswordForm"
      >
        <div class="d-flex">
          <div>
            <label class="input-label">Aktualne hasło</label>
            <v-text-field
              v-model="password.old"
              :rules="[rules.required]"
              type="password"
              placeholder="Wprowadź aktualne hasło"
              v-bind="props.validatedField"
              class="user-settings__input mr-4"
            />
          </div>

          <div>
            <label class="input-label">Nowe hasło</label>
            <v-text-field
              v-model="password.new"
              :rules="[rules.required, rules.password]"
              type="password"
              placeholder="Wprowadź nowe hasło"
              v-bind="props.validatedField"
              class="user-settings__input mr-4"
            />
          </div>

          <div>
            <label class="input-label">Powtórz nowe hasło</label>
            <v-text-field
              v-model="password.repeat"
              :rules="[rules.required, rules.match(password.new)]"
              type="password"
              placeholder="Potwierdź nowe hasło"
              v-bind="props.validatedField"
              class="user-settings__input"
            />
          </div>
        </div>
        <v-btn
          class="custom-submit-button mt-1"
          color="primary"
          type="submit"
        >
          Zmień hasło
        </v-btn>
      </v-form>
    </v-card>
    <v-card class="user-settings__card mt-8">
      <v-card-title class="pa-0">
        Układ widoku projektu
      </v-card-title>

      <v-form
        ref="userLayoutChangeForm"
        class="mt-6"
        @submit.prevent="submitLayoutForm"
      >
        <div>
          <v-radio-group
            v-model="layoutType"
            row
          >
            <v-row>
              <v-col
                cols="12"
                lg="6"
              >
                <v-radio
                  value="default_layout"
                  class="radio-column"
                  :class="{'picked' : layoutType === 'default_layout'}"
                >
                  <template v-slot:label>
                    <div class="ml-3">
                      <h3 class="primary--text radio-column--header mb-1">
                        Domyślny
                      </h3>
                      <span class="primary--text radio-column--text">
                        Domyślny widok projektu. Sekcja z bazą plików umiejscowiona na samym dole.
                      </span>
                    </div>
                  </template>
                </v-radio>
              </v-col>
              <v-col
                cols="12"
                lg="6"
              >
                <v-radio
                  value="graphic_designer_layout"
                  class="radio-column"
                  :class="{'picked' : layoutType === 'graphic_designer_layout'}"
                >
                  <template v-slot:label>
                    <div class="ml-3">
                      <h3 class="primary--text radio-column--header mb-1">
                        Dla grafików
                      </h3>
                      <span class="primary--text radio-column--text">
                        Widok projektu dopasowany do potrzeb grafików. Sekcja z bazą plików umiejscowiona u góry widoku, nad sekcją z zespołami.
                      </span>
                    </div>
                  </template>
                </v-radio>
              </v-col>
            </v-row>
            <div />
          </v-radio-group>
        </div>
        <v-btn
          class="custom-submit-button mt-1"
          color="primary"
          type="submit"
          :disabled="layoutType === formLayoutType"
        >
          Zmień układ
        </v-btn>
      </v-form>
    </v-card>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import props from '@/helpers/componentsProps';
import rules from '@/helpers/validators';

export default {
  data() {
    return {
      props,
      rules,
      isFormValid: null,
      isNotificationSwitchBlocked: false,
      emailNotifications: null,
      chatNotifications: null,
      ownActionsNotifications: null,
      password: {
        old: '',
        new: '',
        repeat: '',
      },
      formLayoutType: '',
    };
  },

  computed: {
    ...mapState('auth', [
      'user',
    ]),
    ...mapState('users', [
      'backendVersion',
    ]),
    layoutType: {
      get() {
        return this.user.layoutType
      },
      set(type) {
        this.formLayoutType = type
      }
    },
    frontendCommitHash() {
      return process.env.VUE_APP_COMMIT_HASH
    },
    backendCommitHash() {
      return this.backendVersion
    }
  },

  created() {
    const { emailNotifications, chatNotifications, ownActionsNotifications, layoutType } = this.user;
    this.emailNotifications = emailNotifications;
    this.chatNotifications = chatNotifications;
    this.ownActionsNotifications = ownActionsNotifications;
    this.formLayoutType = layoutType;
    this.fetchBackendVersion();
  },

  methods: {
    ...mapActions('users', [
      'editUserNotificationsSettings',
      'editUserPassword',
      'fetchBackendVersion',
    ]),
    ...mapActions('auth', [
      'editUserLayout',
    ]),

    changeNotificationsSettings(value, label) {
      const user = {
        id: this.user.id,
        value,
        label,
      };

      this.editUserNotificationsSettings(user)
        .catch(() => {
          this.isNotificationSwitchBlocked = true;
          this.$nextTick(() => {
            this[label] = this.user[label];
            this.isNotificationSwitchBlocked = false;
          });
        });
    },

    submitPasswordForm() {
      this.$refs.userPasswordChangeForm.validate();
      const user = {
        id: this.user.id,
        currentPassword: this.password.old,
        password: this.password.new,
      };

      if (this.isFormValid) {
        this.editUserPassword(user)
          .then(() => {
            this.$refs.userPasswordChangeForm.reset();
          });
      }
    },
    submitLayoutForm() {
      this.editUserLayout(this.formLayoutType)
    },
  },
};
</script>

<style lang="scss" scoped>
.user-settings {
  &__card {
    padding: 24px 32px 20px;
  }
  &__input {
    width: 232px;
  }
  &::v-deep .v-input--selection-controls {
    margin-top:0;
    padding-top:0;
  }
  .input-label {
    margin-left:4px;
  }
  .radio-column {
    padding: 24px;
    border: 1px solid $secondary;
    height:100%;
    align-items:flex-start;
    &--header {
      font-size: 16px;
      line-height: 24px;
      font-weight: 600;
    }
    &--text {
      font-size: 14px;
      line-height: 20px;
    }
    &.picked {
      border: 1px solid $primary;

    }
  }
}
</style>
